// @ts-check

/**
 * @typedef {{
 *   isIdle: () => boolean,
 *   idleCheckIntervalMs?: number,
 * }} ServiceWorkerReloadOptions
 */

/** @type {Promise<void> | null} */
let setupPromise = null;

/**
 * @param {ServiceWorkerReloadOptions} options
 * @returns {Promise<void>}
 */
export async function setupServiceWorkerReloadWhenIdle(options) {
  if (setupPromise !== null) {
    await setupPromise;
    return;
  }

  setupPromise = setupServiceWorkerReloadWhenIdleOnce(options);
  await setupPromise;
}

/**
 * @param {ServiceWorkerReloadOptions} options
 * @returns {Promise<void>}
 */
async function setupServiceWorkerReloadWhenIdleOnce(options) {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  let isServiceWorkerRefreshing = false;
  let activeController = navigator.serviceWorker.controller;
  /** @type {Promise<void> | null} */
  let safeActivationCheckPromise = null;
  const idleCheckIntervalMs = options.idleCheckIntervalMs || 1000;

  navigator.serviceWorker.addEventListener("controllerchange", function () {
    const nextController = navigator.serviceWorker.controller;

    if (!activeController) {
      activeController = nextController;
      return;
    }

    if (nextController === activeController) {
      return;
    }

    activeController = nextController;

    if (isServiceWorkerRefreshing) {
      return;
    }

    isServiceWorkerRefreshing = true;
    window.location.reload();
  });

  try {
    await navigator.serviceWorker.ready;
  } catch (error) {
    console.error("[app] service worker not ready", error);
    return;
  }

  async function runSafeActivationCheck() {
    if (safeActivationCheckPromise) {
      await safeActivationCheckPromise;
      return;
    }

    safeActivationCheckPromise = runSafeActivationCheckOnce();
    try {
      await safeActivationCheckPromise;
    } finally {
      safeActivationCheckPromise = null;
    }
  }

  async function runSafeActivationCheckOnce() {
    const registration = await getServiceWorkerRegistration();
    if (!registration) {
      return;
    }

    await tryActivateWaitingWorker(registration, options, idleCheckIntervalMs);
  }

  const registration = await getServiceWorkerRegistration();
  if (!registration) {
    return;
  }

  const updateRegistration = registration;

  async function runBackgroundUpdateCheck() {
    await requestServiceWorkerUpdate(updateRegistration);
    await runSafeActivationCheck();
  }

  /**
   * @param {ServiceWorker} installingWorker
   * @returns {void}
   */
  function watchInstallingWorker(installingWorker) {
    installingWorker.addEventListener("statechange", function () {
      if (installingWorker.state !== "installed") {
        return;
      }

      void runSafeActivationCheck();
    });
  }

  function watchRegistrationInstallingWorker() {
    const installingWorker = updateRegistration.installing;
    if (!installingWorker) {
      return;
    }

    watchInstallingWorker(installingWorker);
  }

  updateRegistration.addEventListener(
    "updatefound",
    watchRegistrationInstallingWorker,
  );
  watchRegistrationInstallingWorker();

  await runSafeActivationCheck();
  void runBackgroundUpdateCheck();

  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState !== "visible") {
      return;
    }

    void runSafeActivationCheck();
    void runBackgroundUpdateCheck();
  });

  window.addEventListener("pageshow", function () {
    if (document.visibilityState !== "visible") {
      return;
    }

    void runSafeActivationCheck();
    void runBackgroundUpdateCheck();
  });
}

/**
 * @returns {boolean}
 */
function canActivateWaitingWorker() {
  let result = true;

  if (document.visibilityState !== "visible") {
    result = false;
  }

  return result;
}

/**
 * @returns {Promise<ServiceWorkerRegistration | null>}
 */
async function getServiceWorkerRegistration() {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      return null;
    }

    return registration;
  } catch (error) {
    console.error("[app] failed to get service worker registration", error);
  }

  return null;
}

/**
 * @param {ServiceWorkerRegistration} registration
 * @param {ServiceWorkerReloadOptions} options
 * @param {number} idleCheckIntervalMs
 * @returns {Promise<void>}
 */
async function tryActivateWaitingWorker(
  registration,
  options,
  idleCheckIntervalMs,
) {
  let waitingWorker = registration.waiting;

  if (!waitingWorker) {
    return;
  }

  if (!canActivateWaitingWorker()) {
    return;
  }

  await waitForAppIdle(options, idleCheckIntervalMs);

  if (!canActivateWaitingWorker()) {
    return;
  }

  const latestRegistration = await getServiceWorkerRegistration();
  waitingWorker = latestRegistration?.waiting || null;

  if (!waitingWorker) {
    return;
  }

  waitingWorker.postMessage({ type: "SKIP_WAITING" });
}

/**
 * @param {ServiceWorkerReloadOptions} options
 * @param {number} idleCheckIntervalMs
 * @returns {Promise<void>}
 */
async function waitForAppIdle(options, idleCheckIntervalMs) {
  while (!options.isIdle()) {
    await delay(idleCheckIntervalMs);
  }
}

/**
 * @param {number} durationMs
 * @returns {Promise<void>}
 */
function delay(durationMs) {
  return new Promise(function (resolve) {
    window.setTimeout(resolve, durationMs);
  });
}

/**
 * @param {ServiceWorkerRegistration} registration
 * @returns {Promise<void>}
 */
async function requestServiceWorkerUpdate(registration) {
  try {
    await registration.update();
  } catch (error) {
    console.debug("[app] service worker update check failed", error);
  }
}
